import axios, { AxiosResponse } from 'axios'
import { Enum } from '../../../../../_metronic/helpers'
import { FuncionarioView } from '../../funcionarios/funcionarios-list/core/_models'
import {
  AgendaEmpresaResponse,
  AgendaPessoaResponse,
  EscalaAgendaDtoAlteracaoEmpresa,
  EscalaAgendaDtoAlteracaoPessoa,
  EscalaAgendaViewEmpresa,
  EscalaAgendaViewPessoa,
  EscalaDtoCadastroEmpresa,
  EscalaDtoCadastroPessoa,
  EscalaViewEmpresa,
  EscalaViewPessoa,
  EscalasQueryResponse,
  FeriadoDtoAlteracao,
  FeriadoDtoCadastro,
  FeriadoView,
  FeriadosQueryResponse,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const ESCALAS_URL = `${API_URL}/escalas`
const ESCALAS_EMPRESA_URL = `${API_URL}/escalas/empresa`
const FERIADOS_URL = `${API_URL}/feriados`

/* -------------------------------------------------------------------------- */
/* Eixo pessoal — /escalas                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Lista paginada da escala da pessoa logada.
 *
 * ⚠️ **Não** inclua `pessoa:<id>` no `search`: o `EscalaController` já concatena
 * `"pessoa:<id da pessoa logada>," + search` antes de consultar. Mandar de novo
 * gera filtro duplicado e devolve menos linha do que o usuário espera.
 */
export const getEscalas = (query?: string): Promise<EscalasQueryResponse> => {
  return axios
    .get(`${ESCALAS_URL}?${query ?? ''}`)
    .then((d: AxiosResponse<EscalasQueryResponse>) => d.data)
}

export const getEscalaById = (id: number): Promise<EscalaViewPessoa> => {
  return axios.get(`${ESCALAS_URL}/${id}`).then((d: AxiosResponse<EscalaViewPessoa>) => d.data)
}

export const getAgendaPessoa = (
  inicio: string,
  termino: string,
  empresa?: number,
  especialidade?: number
): Promise<AgendaPessoaResponse> => {
  const params = new URLSearchParams({ inicio, termino })
  if (empresa) params.set('empresa', String(empresa))
  if (especialidade) params.set('especialidade', String(especialidade))

  return axios
    .get(`${ESCALAS_URL}/agenda?${params.toString()}`)
    .then((d: AxiosResponse<AgendaPessoaResponse>) => d.data)
}

export const getAgendaPessoaById = (id: number): Promise<EscalaAgendaViewPessoa> => {
  return axios
    .get(`${ESCALAS_URL}/agenda/${id}`)
    .then((d: AxiosResponse<EscalaAgendaViewPessoa>) => d.data)
}

export const createEscalaPessoa = (dto: EscalaDtoCadastroPessoa): Promise<EscalaViewPessoa> => {
  return axios.post(ESCALAS_URL, dto).then((d: AxiosResponse<EscalaViewPessoa>) => d.data)
}

export const updateAgendaPessoa = (
  dto: EscalaAgendaDtoAlteracaoPessoa
): Promise<EscalaAgendaViewPessoa> => {
  return axios
    .put(`${ESCALAS_URL}/agenda`, dto)
    .then((d: AxiosResponse<EscalaAgendaViewPessoa>) => d.data)
}

export const deleteEscalaPessoa = (id: number): Promise<void> => {
  return axios.delete(`${ESCALAS_URL}/${id}`).then(() => undefined)
}

export const deleteAgendasPessoa = (ids: number[]): Promise<void> => {
  return axios.delete(`${ESCALAS_URL}/agenda?ids=${ids.join(',')}`).then(() => undefined)
}

/* -------------------------------------------------------------------------- */
/* Eixo empresa — /escalas/empresa                                            */
/* -------------------------------------------------------------------------- */

export const getEscalasEmpresa = (
  empresa: number,
  query?: string
): Promise<EscalasQueryResponse> => {
  return axios
    .get(`${ESCALAS_EMPRESA_URL}/${empresa}?${query ?? ''}`)
    .then((d: AxiosResponse<EscalasQueryResponse>) => d.data)
}

export const getEscalaEmpresaById = (empresa: number, id: number): Promise<EscalaViewEmpresa> => {
  return axios
    .get(`${ESCALAS_EMPRESA_URL}/${empresa}/${id}`)
    .then((d: AxiosResponse<EscalaViewEmpresa>) => d.data)
}

export const getAgendaEmpresa = (
  empresa: number,
  inicio: string,
  termino: string,
  especialidade?: number
): Promise<AgendaEmpresaResponse> => {
  const params = new URLSearchParams({ inicio, termino })
  if (especialidade) params.set('especialidade', String(especialidade))

  return axios
    .get(`${ESCALAS_EMPRESA_URL}/agenda/${empresa}?${params.toString()}`)
    .then((d: AxiosResponse<AgendaEmpresaResponse>) => d.data)
}

export const createEscalaEmpresa = (
  empresa: number,
  dto: EscalaDtoCadastroEmpresa
): Promise<EscalaViewEmpresa> => {
  return axios
    .post(`${ESCALAS_EMPRESA_URL}/${empresa}`, dto)
    .then((d: AxiosResponse<EscalaViewEmpresa>) => d.data)
}

export const updateAgendaEmpresa = (
  empresa: number,
  dto: EscalaAgendaDtoAlteracaoEmpresa
): Promise<EscalaAgendaViewEmpresa> => {
  return axios
    .put(`${ESCALAS_EMPRESA_URL}/agenda/${empresa}`, dto)
    .then((d: AxiosResponse<EscalaAgendaViewEmpresa>) => d.data)
}

export const deleteEscalaEmpresa = (empresa: number, id: number): Promise<void> => {
  return axios.delete(`${ESCALAS_EMPRESA_URL}/${empresa}/${id}`).then(() => undefined)
}

export const deleteAgendasEmpresa = (empresa: number, ids: number[]): Promise<void> => {
  return axios
    .delete(`${ESCALAS_EMPRESA_URL}/agenda/${empresa}?ids=${ids.join(',')}`)
    .then(() => undefined)
}

/* -------------------------------------------------------------------------- */
/* Feriados — /feriados                                                       */
/* -------------------------------------------------------------------------- */

export const getFeriados = (query?: string): Promise<FeriadosQueryResponse> => {
  return axios
    .get(`${FERIADOS_URL}?${query ?? ''}`)
    .then((d: AxiosResponse<FeriadosQueryResponse>) => d.data)
}

export const getFeriadoById = (id: number): Promise<FeriadoView> => {
  return axios.get(`${FERIADOS_URL}/${id}`).then((d: AxiosResponse<FeriadoView>) => d.data)
}

export const createFeriadoEmpresa = (
  empresa: number,
  dto: FeriadoDtoCadastro
): Promise<FeriadoView[]> => {
  return axios
    .post(`${FERIADOS_URL}/empresa/${empresa}`, dto)
    .then((d: AxiosResponse<FeriadoView[]>) => d.data)
}

export const updateFeriado = (id: number, dto: FeriadoDtoAlteracao): Promise<FeriadoView> => {
  return axios.put(`${FERIADOS_URL}/${id}`, dto).then((d: AxiosResponse<FeriadoView>) => d.data)
}

export const deleteFeriado = (id: number): Promise<void> => {
  return axios.delete(`${FERIADOS_URL}/${id}`).then(() => undefined)
}

/* -------------------------------------------------------------------------- */
/* Apoio aos seletores do formulário                                          */
/* -------------------------------------------------------------------------- */

/**
 * Vínculos (`Funcionario`) da pessoa logada — fonte das empresas e
 * especialidades oferecidas no eixo pessoal. É a mesma relação que o backend usa
 * em `processaVinculos()` para aceitar ou recusar o vínculo pedido.
 */
export const getMeusVinculos = (): Promise<FuncionarioView[]> => {
  return axios
    .get(`${API_URL}/funcionarios/me`)
    .then((d: AxiosResponse<FuncionarioView[]>) => d.data)
}

/** Vínculos de um profissional numa empresa — especialidades do eixo empresa. */
export const getVinculosPorEmpresaEPessoa = (
  empresa: number,
  pessoa: number
): Promise<FuncionarioView[]> => {
  return axios
    .get(`${API_URL}/funcionarios/empresa/${empresa}/pessoa/${pessoa}`)
    .then((d: AxiosResponse<FuncionarioView[]>) => d.data)
}

/** `GET /tipos/escala` — `DISPONIBILIDADE` / `INDISPONIBILIDADE`. */
export const getTiposEscala = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/tipos/escala`).then((d: AxiosResponse<Enum[]>) => d.data)
}

/** `GET /tipos/feriado` — `NACIONAL` / `ESTADUAL` / `MUNICIPAL` / `INSTITUCIONAL`. */
export const getTiposFeriado = (): Promise<Enum[]> => {
  return axios.get(`${API_URL}/tipos/feriado`).then((d: AxiosResponse<Enum[]>) => d.data)
}
